// API configuration
const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://server.diamondinformationsystems.com";

// Authentication service class for handling all auth-related API calls
class AuthService {
  constructor() {
    this.baseURL = `${API_BASE_URL}/api/auth`;
    this.token = localStorage.getItem("authToken");
  }

  // Login user
  async login(email, password) {
    try {
      const response = await fetch(`${this.baseURL}/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (data.success) {
        // Store token in localStorage
        this.token = data.token;
        localStorage.setItem("authToken", data.token);
        localStorage.setItem("user", JSON.stringify(data.user));

        return {
          success: true,
          data: data.user,
          token: data.token,
          message: data.message,
        };
      } else {
        return {
          success: false,
          data: null,
          message: data.message || "Login failed",
        };
      }
    } catch (error) {
      console.error("Login error:", error);
      return {
        success: false,
        data: null,
        message: "Network error. Please check your connection.",
        error: error.message,
      };
    }
  }

  // Logout user
  async logout() {
    try {
      // Call backend logout endpoint if available
      if (this.token) {
        await fetch(`${this.baseURL}/logout`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.token}`,
          },
        });
      }
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      // Clear local storage regardless of backend response
      this.token = null;
      localStorage.removeItem("authToken");
      localStorage.removeItem("user");

      return {
        success: true,
        message: "Logged out successfully",
      };
    }
  }

  // Get current user data
  async getCurrentUser() {
    try {
      if (!this.token) {
        return {
          success: false,
          data: null,
          message: "No authentication token found",
        };
      }

      const response = await fetch(`${this.baseURL}/me`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.token}`,
        },
      });

      const data = await response.json();

      if (data.success) {
        // Update stored user data
        localStorage.setItem("user", JSON.stringify(data.user));
        return {
          success: true,
          data: data.user,
          message: "User data retrieved successfully",
        };
      } else {
        return {
          success: false,
          data: null,
          message: data.message || "Failed to get user data",
        };
      }
    } catch (error) {
      console.error("Get current user error:", error);
      return {
        success: false,
        data: null,
        message: "Failed to get user information",
        error: error.message,
      };
    }
  }

  // Change password
  async changePassword(currentPassword, newPassword) {
    try {
      if (!this.token) {
        return {
          success: false,
          message: "No authentication token found",
        };
      }

      const response = await fetch(`${this.baseURL}/change-password`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.token}`,
        },
        body: JSON.stringify({
          currentPassword,
          newPassword,
        }),
      });

      const data = await response.json();
      return {
        success: data.success,
        message:
          data.message ||
          (data.success
            ? "Password changed successfully"
            : "Failed to change password"),
      };
    } catch (error) {
      console.error("Change password error:", error);
      return {
        success: false,
        message: "Failed to change password",
        error: error.message,
      };
    }
  }

  // Update user profile
  async updateProfile(profileData) {
    try {
      if (!this.token) {
        return {
          success: false,
          message: "No authentication token found",
        };
      }

      const response = await fetch(`${this.baseURL}/profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.token}`,
        },
        body: JSON.stringify(profileData),
      });

      const data = await response.json();

      if (data.success && data.user) {
        // Update stored user data
        localStorage.setItem("user", JSON.stringify(data.user));
        return {
          success: true,
          data: data.user,
          message: data.message || "Profile updated successfully",
        };
      }

      return {
        success: data.success,
        message: data.message || "Failed to update profile",
      };
    } catch (error) {
      console.error("Update profile error:", error);
      return {
        success: false,
        message: "Failed to update profile",
        error: error.message,
      };
    }
  }

  // Check if user is authenticated
  isAuthenticated() {
    return !!this.token && !!localStorage.getItem("user");
  }

  // Get stored user data
  getStoredUser() {
    const userStr = localStorage.getItem("user");
    return userStr ? JSON.parse(userStr) : null;
  }

  // Get stored token
  getToken() {
    return this.token || localStorage.getItem("authToken");
  }

  // Set token manually (useful for testing or manual auth)
  setToken(token) {
    this.token = token;
    localStorage.setItem("authToken", token);
  }

  // Clear all auth data
  clearAuth() {
    this.token = null;
    localStorage.removeItem("authToken");
    localStorage.removeItem("user");
  }
}

// Create and export a singleton instance
const authService = new AuthService();
export default authService;

// Also export the class for advanced use cases
export { AuthService };
